import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Mountain, Users, Calendar, FolderOpen, 
  BookOpen, Images, Bed, MessageSquare, Heart, Settings,
  LogOut, Menu, X, Search, Plus, Edit, Trash2, Upload, Save, Eye
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage, LANGUAGES } from '../../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { ImageUploader } from '../ImageUpload';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDEBAR_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Mountain, label: 'Falesie', path: '/admin/crags' },
  { icon: Users, label: 'Team', path: '/admin/team' },
  { icon: Calendar, label: 'Eventi', path: '/admin/events' },
  { icon: FolderOpen, label: 'Progetti', path: '/admin/projects' },
  { icon: BookOpen, label: 'Blog', path: '/admin/blog' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Bed, label: 'Alloggi', path: '/admin/accommodations' },
  { icon: MessageSquare, label: 'Messaggi', path: '/admin/messages' },
  { icon: Heart, label: 'Donazioni', path: '/admin/donations' },
  { icon: Search, label: 'SEO', path: '/admin/seo' },
  { icon: Upload, label: 'Uploads', path: '/admin/uploads' },
  { icon: Settings, label: 'Impostazioni', path: '/admin/settings' },
];

function AdminSidebar({ currentPath, sidebarOpen, setSidebarOpen, logout }) {
  return (
    <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0A0A] transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
      <div className="flex items-center justify-between h-16 px-6 border-b border-gray-800">
        <Link to="/" className="text-white font-heading text-lg uppercase">CMS Admin</Link>
        <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
          <X className="w-6 h-6" />
        </button>
      </div>
      <nav className="p-4 space-y-1 max-h-[calc(100vh-8rem)] overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              currentPath === item.path ? 'bg-[#FF6D00] text-white' : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-sm">{item.label}</span>
          </Link>
        ))}
      </nav>
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
        <button onClick={logout} className="flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-white w-full">
          <LogOut className="w-5 h-5" />
          <span className="text-sm">Logout</span>
        </button>
      </div>
    </aside>
  );
}

function TranslatedInput({ label, value, onChange, placeholder, multiline = false }) {
  const [activeLang, setActiveLang] = useState('it');
  const Component = multiline ? Textarea : Input;
  
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">{label}</label>
      <Tabs value={activeLang} onValueChange={setActiveLang}>
        <TabsList className="h-8">
          {Object.values(LANGUAGES).map(lang => (
            <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-2 py-1">
              {lang.flag}
            </TabsTrigger>
          ))}
        </TabsList>
        {Object.values(LANGUAGES).map(lang => (
          <TabsContent key={lang.code} value={lang.code} className="mt-2">
            <Component
              value={value?.[lang.code] || ''}
              onChange={(e) => onChange({ ...value, [lang.code]: e.target.value })}
              placeholder={`${placeholder} (${lang.name})`}
              rows={multiline ? 4 : undefined}
            />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

// ==================== ADMIN CRAGS ====================
export function AdminCrags() {
  const { logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [crags, setCrags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCrag, setEditingCrag] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showRoutesDialog, setShowRoutesDialog] = useState(false);
  const [selectedCragRoutes, setSelectedCragRoutes] = useState([]);
  const [selectedCragId, setSelectedCragId] = useState(null);
  const [selectedCragName, setSelectedCragName] = useState('');
  const [editingRoute, setEditingRoute] = useState(null);
  const [showRouteDialog, setShowRouteDialog] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/admin/login'); return; }
    fetchCrags();
  }, [isAuthenticated, authLoading, navigate]);

  const fetchCrags = async () => {
    try {
      const res = await axios.get(`${API}/crags?active_only=false`);
      setCrags(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoutes = async (cragId, cragName = '') => {
    try {
      const res = await axios.get(`${API}/routes?crag_id=${cragId}`);
      setSelectedCragRoutes(res.data);
      setSelectedCragId(cragId);
      setSelectedCragName(cragName);
      setShowRoutesDialog(true);
    } catch (error) {
      toast.error('Errore nel caricamento vie');
    }
  };

  const openNewRoute = () => {
    setEditingRoute({
      name: '',
      grade: '',
      length: '',
      route_type: 'sport',
      bolting: '',
      sector: '',
      description: { it: '' },
      crag_id: selectedCragId
    });
    setShowRouteDialog(true);
  };

  const handleSaveRoute = async () => {
    try {
      if (editingRoute.id) {
        await axios.put(`${API}/routes/${editingRoute.id}`, editingRoute);
        toast.success('Via aggiornata');
      } else {
        await axios.post(`${API}/routes`, editingRoute);
        toast.success('Via creata');
      }
      setShowRouteDialog(false);
      fetchRoutes(selectedCragId, selectedCragName);
      fetchCrags();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!window.confirm('Eliminare questa via?')) return;
    try {
      await axios.delete(`${API}/routes/${routeId}`);
      toast.success('Via eliminata');
      fetchRoutes(selectedCragId, selectedCragName);
      fetchCrags();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleSave = async () => {
    try {
      if (editingCrag.id) {
        await axios.put(`${API}/crags/${editingCrag.id}`, editingCrag);
        toast.success('Falesia aggiornata');
      } else {
        await axios.post(`${API}/crags`, editingCrag);
        toast.success('Falesia creata');
      }
      setShowDialog(false);
      fetchCrags();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa falesia?')) return;
    try {
      await axios.delete(`${API}/crags/${id}`);
      toast.success('Falesia eliminata');
      fetchCrags();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCragId) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/routes/import-csv/${selectedCragId}`, formData);
      toast.success(`Importate ${res.data.count} vie`);
      fetchRoutes(selectedCragId);
      fetchCrags();
    } catch (error) {
      toast.error('Errore nell\'importazione CSV');
    }
    e.target.value = '';
  };

  const openNewCrag = () => {
    setEditingCrag({
      name: '',
      slug: '',
      description: { it: '', en: '', fr: '', de: '', es: '' },
      grade_range: '',
      exposure: { it: '', en: '', fr: '', de: '', es: '' },
      rock_type: { it: '', en: '', fr: '', de: '', es: '' },
      approach_time: { it: '', en: '', fr: '', de: '', es: '' },
      path_type: { it: '', en: '', fr: '', de: '', es: '' },
      gps_lat: 37.8221,
      gps_lng: 13.8919,
      images: [],
      is_active: true
    });
    setShowDialog(true);
  };

  if (authLoading || !isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebar currentPath="/admin/crags" sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} logout={logout} />
      
      <div className="lg:ml-64">
        <header className="bg-white border-b h-16 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-heading text-xl uppercase">Gestione Falesie</h1>
          <Button onClick={openNewCrag} className="btn-primary"><Plus className="w-4 h-4 mr-2" />Nuova Falesia</Button>
        </header>

        <main className="p-6">
          {loading ? (
            <div className="flex justify-center py-20"><div className="animate-spin w-8 h-8 border-4 border-[#FF6D00] border-t-transparent rounded-full" /></div>
          ) : (
            <div className="bg-white rounded-lg border overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Nome</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Slug</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Vie</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Gradi</th>
                    <th className="text-left py-4 px-6 font-heading uppercase text-sm">Stato</th>
                    <th className="text-right py-4 px-6 font-heading uppercase text-sm">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {crags.map((crag) => (
                    <tr key={crag.id} className="border-b hover:bg-gray-50">
                      <td className="py-4 px-6 font-medium">{crag.name}</td>
                      <td className="py-4 px-6 text-gray-500">{crag.slug}</td>
                      <td className="py-4 px-6">
                        <button onClick={() => fetchRoutes(crag.id, crag.name)} className="text-[#FF6D00] hover:underline">
                          {crag.total_routes || 0} vie
                        </button>
                      </td>
                      <td className="py-4 px-6">{crag.grade_range}</td>
                      <td className="py-4 px-6">
                        <span className={`px-2 py-1 rounded text-xs ${crag.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {crag.is_active ? 'Attiva' : 'Nascosta'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end gap-2">
                          <Link to={`/crags/${crag.slug}`} target="_blank" className="p-2 hover:bg-gray-100 rounded">
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Link>
                          <button onClick={() => { setEditingCrag(crag); setShowDialog(true); }} className="p-2 hover:bg-gray-100 rounded">
                            <Edit className="w-4 h-4 text-blue-600" />
                          </button>
                          <button onClick={() => handleDelete(crag.id)} className="p-2 hover:bg-gray-100 rounded">
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </main>
      </div>

      {/* Edit Crag Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCrag?.id ? 'Modifica Falesia' : 'Nuova Falesia'}</DialogTitle>
          </DialogHeader>
          
          {editingCrag && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome</label>
                  <Input value={editingCrag.name} onChange={(e) => setEditingCrag({...editingCrag, name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Slug (URL)</label>
                  <Input value={editingCrag.slug} onChange={(e) => setEditingCrag({...editingCrag, slug: e.target.value})} />
                </div>
              </div>

              <TranslatedInput label="Descrizione" value={editingCrag.description} onChange={(v) => setEditingCrag({...editingCrag, description: v})} placeholder="Descrizione" multiline />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Range Gradi</label>
                  <Input value={editingCrag.grade_range} onChange={(e) => setEditingCrag({...editingCrag, grade_range: e.target.value})} placeholder="es. 5a - 7c" />
                </div>
                <TranslatedInput label="Esposizione" value={editingCrag.exposure} onChange={(v) => setEditingCrag({...editingCrag, exposure: v})} placeholder="es. Sud-Est" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TranslatedInput label="Tipo di roccia" value={editingCrag.rock_type} onChange={(v) => setEditingCrag({...editingCrag, rock_type: v})} placeholder="es. Calcare" />
                <TranslatedInput label="Tempo avvicinamento" value={editingCrag.approach_time} onChange={(v) => setEditingCrag({...editingCrag, approach_time: v})} placeholder="es. 15 minuti" />
              </div>

              <TranslatedInput label="Tipo sentiero" value={editingCrag.path_type} onChange={(v) => setEditingCrag({...editingCrag, path_type: v})} placeholder="es. Sentiero segnato" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">GPS Latitudine</label>
                  <Input type="number" step="0.0001" value={editingCrag.gps_lat} onChange={(e) => setEditingCrag({...editingCrag, gps_lat: parseFloat(e.target.value)})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">GPS Longitudine</label>
                  <Input type="number" step="0.0001" value={editingCrag.gps_lng} onChange={(e) => setEditingCrag({...editingCrag, gps_lng: parseFloat(e.target.value)})} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Immagini</label>
                <ImageUploader 
                  value={editingCrag.images} 
                  onChange={(images) => setEditingCrag({...editingCrag, images: images.map(i => typeof i === 'string' ? i : i.url)})}
                  category="crags"
                  maxImages={5}
                />
              </div>

              <div className="flex items-center gap-2">
                <input type="checkbox" id="is_active" checked={editingCrag.is_active} onChange={(e) => setEditingCrag({...editingCrag, is_active: e.target.checked})} />
                <label htmlFor="is_active" className="text-sm">Falesia attiva (visibile sul sito)</label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Annulla</Button>
            <Button onClick={handleSave} className="btn-primary"><Save className="w-4 h-4 mr-2" />Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Routes Dialog */}
      <Dialog open={showRoutesDialog} onOpenChange={setShowRoutesDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vie di {selectedCragName} ({selectedCragRoutes.length})</DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex gap-4 mb-4 flex-wrap">
              <Button onClick={openNewRoute} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nuova Via
              </Button>
              <label className="inline-flex items-center px-4 py-2 border border-[#FF6D00] text-[#FF6D00] rounded-lg cursor-pointer hover:bg-[#FF6D00]/10">
                <Upload className="w-4 h-4 mr-2" />
                Importa CSV
                <input type="file" accept=".csv" onChange={handleCSVUpload} className="hidden" />
              </label>
              <p className="text-sm text-gray-500 self-center">
                CSV: name,grade,length,route_type,bolting,sector,notes
              </p>
            </div>

            {selectedCragRoutes.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left py-3 px-4 text-sm font-semibold">Nome</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold">Grado</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold">Lunghezza</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold">Tipo</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold">Settore</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold">Chiodatura</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedCragRoutes.map((route) => (
                      <tr key={route.id} className="border-t hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{route.name}</td>
                        <td className="py-3 px-4">
                          <span className="bg-[#FF6D00]/10 text-[#FF6D00] px-2 py-1 rounded text-sm font-semibold">
                            {route.grade}
                          </span>
                        </td>
                        <td className="py-3 px-4">{route.length || '-'}</td>
                        <td className="py-3 px-4 capitalize">{route.route_type}</td>
                        <td className="py-3 px-4">{route.sector || '-'}</td>
                        <td className="py-3 px-4">{route.bolting || '-'}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            <button 
                              onClick={() => { setEditingRoute(route); setShowRouteDialog(true); }} 
                              className="p-2 hover:bg-gray-100 rounded"
                              title="Modifica"
                            >
                              <Edit className="w-4 h-4 text-blue-600" />
                            </button>
                            <button 
                              onClick={() => handleDeleteRoute(route.id)} 
                              className="p-2 hover:bg-gray-100 rounded"
                              title="Elimina"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Mountain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">Nessuna via registrata</p>
                <Button onClick={openNewRoute} className="btn-primary">
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi la prima via
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit/Add Route Dialog */}
      <Dialog open={showRouteDialog} onOpenChange={setShowRouteDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingRoute?.id ? 'Modifica Via' : 'Nuova Via'}</DialogTitle>
          </DialogHeader>
          
          {editingRoute && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome via *</label>
                  <Input 
                    value={editingRoute.name} 
                    onChange={(e) => setEditingRoute({...editingRoute, name: e.target.value})}
                    placeholder="es. Luna China"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Grado *</label>
                  <Input 
                    value={editingRoute.grade} 
                    onChange={(e) => setEditingRoute({...editingRoute, grade: e.target.value})}
                    placeholder="es. 7C"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Lunghezza</label>
                  <Input 
                    value={editingRoute.length || ''} 
                    onChange={(e) => setEditingRoute({...editingRoute, length: e.target.value})}
                    placeholder="es. 18m"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tipo</label>
                  <Select 
                    value={editingRoute.route_type || 'sport'} 
                    onValueChange={(v) => setEditingRoute({...editingRoute, route_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sport">Sport</SelectItem>
                      <SelectItem value="trad">Trad</SelectItem>
                      <SelectItem value="boulder">Boulder</SelectItem>
                      <SelectItem value="multipitch">Multipitch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Settore</label>
                  <Input 
                    value={editingRoute.sector || ''} 
                    onChange={(e) => setEditingRoute({...editingRoute, sector: e.target.value})}
                    placeholder="es. A, B, Superiore"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Chiodatura</label>
                <Input 
                  value={editingRoute.bolting || ''} 
                  onChange={(e) => setEditingRoute({...editingRoute, bolting: e.target.value})}
                  placeholder="es. 8 spit, catena"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descrizione</label>
                <Textarea 
                  value={editingRoute.description?.it || ''} 
                  onChange={(e) => setEditingRoute({
                    ...editingRoute, 
                    description: {...editingRoute.description, it: e.target.value}
                  })}
                  placeholder="Descrizione della via..."
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRouteDialog(false)}>Annulla</Button>
            <Button onClick={handleSaveRoute} className="btn-primary">
              <Save className="w-4 h-4 mr-2" />
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
